/* eslint-disable react/prop-types */
import { precisionFixed } from 'd3';
import dayjs from 'dayjs';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import { LineChart } from 'echarts/charts';
import {
  GridComponent,
  LegendComponent,
  TitleComponent,
  TooltipComponent
} from 'echarts/components';
import * as echarts from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { getColor, getPastDates, rgbaColor } from 'helpers/utils';
import PropTypes from 'prop-types';
import React, { useEffect, useRef } from 'react';

echarts.use([
  TitleComponent,
  TooltipComponent,
  GridComponent,
  LineChart,
  CanvasRenderer,
  LegendComponent
]);

const tooltipFormatter = (params, Series, precision, unit, charColors) => {
  return `<div>
    <h6 class="fs--2 mb-0 fw-semi-bold">${params[0].axisValueLabel}</h6>
    <div class="align-items-center">
      ${Series.map((series, index) => {
        const dataIndex = params.findIndex(p => p.seriesIndex === index);
        const value =
          dataIndex !== -1 &&
          params[dataIndex]?.data != null &&
          params[dataIndex]?.data !== ''
            ? params[dataIndex]?.data?.toFixed(precision)
            : 'N/A';

        const isMeanOrAvg = series === 'Mean' || series === 'Average';
        const color = charColors[index];

        // Check if value is not 'N/A' before rendering
        if (value !== 'N/A') {
          return `
            <p class='mb-0 text-600 fs--1'>
              <span style='display: inline-block; width: 10px; height: 10px; border-radius: 50%; background-color: ${color}; margin-right: 5px;'></span>
              ${series}: <span class='text-800 fw-semi-bold fs--1'>${value} <span class='text-500 fw-normal fs--1'>${unit}</span></span>
            </p>`;
        }
        return ''; // Return empty string if value is 'N/A'
      }).join('')}
    </div>
  </div>`;
};

const getOptions = (data, xAxis, Series, unit, decimalPoint) => {
  // let charColors = ['danger', 'info', 'warning', 'success'];
  const charColors = ['#5470C6', '#ffd000', '#ff4f4f', '#91cc75', '#c966ed'];

  // const primaryColor = getColor('primary');
  const primaryColor = '#c966ed';

  const seriesConfig = Series.map((metric, index) => {
    // const isLastSeries = index === Series.length - 1;
    const lineStyle = {
      type: 'solid',
      width: 1.5,
      color: charColors[index]
    };

    if (metric == 'Mean' || metric === 'Average') {
      return {
        type: 'line',
        name: metric,
        data: data[index],
        showSymbol: 5,
        symbol: 'circle',
        itemStyle: {
          borderColor: primaryColor
        },
        lineStyle: {
          color: primaryColor,
          width: 1.5,
          type: 'solid'
        }
      };
    } else {
      return {
        name: metric,
        type: 'line',
        data: data[index],
        showSymbol: 5,
        symbol: 'circle',
        lineStyle
      };
    }
  });

  return {
    // color: [...charColors, primaryColor],
    color: charColors,
    tooltip: {
      trigger: 'axis',
      padding: [7, 10],
      backgroundColor: getColor('gray-100'),
      borderColor: getColor('gray-300'),
      textStyle: { color: getColor('dark') },
      borderWidth: 1,
      transitionDuration: 0,
      valueFormatter: function (value) {
        return value.toFixed(decimalPoint) + ` ${unit}`;
      }
    },
    legend: {
      data: Series,
      textStyle: {
        color: getColor('gray-700')
      },
      left: 10,
      top: 0,
      icon: 'circle',
      inactiveColor: getColor('gray-400')
    },
    xAxis: {
      type: 'category',
      data: xAxis,
      boundaryGap: false,
      axisLine: {
        lineStyle: {
          color: getColor('gray-300')
        }
      },
      axisTick: { show: true },
      axisLabel: {
        color: '#87848A',
        margin: 5,
        showMaxLabel: true
      },
      splitLine: {
        show: false
      },
      axisPointer: {
        lineStyle: {
          color: getColor('gray-300')
        }
      }
    },
    yAxis: {
      position: 'right',
      name: unit,
      axisPointer: { type: 'none' },
      axisTick: 'none',
      splitLine: {
        lineStyle: {
          color: getColor('gray-200')
        }
      },
      axisLine: { show: false },
      axisLabel: { color: '#87848A' }
    },
    series: seriesConfig,
    grid: {
      containLabel: true,
      right: '10px',
      left: '15px',
      bottom: '0px',
      top: '10%'
    }
  };
};

const LineChartBody = ({ chartdata, label, onLegendSelect }) => {
  const { data, xAxis, Series, unit } = chartdata;
  const chartRef = useRef(null);

  const onChartReady = chart => {
    chart.on('legendselectchanged', params => {
      const selectedLegend = Object.keys(params.selected).filter(
        key => params.selected[key]
      );
      if (selectedLegend.length > 0) {
        onLegendSelect(selectedLegend[selectedLegend.length - 1]);
      }
    });
  };

  useEffect(() => {
    if (chartRef.current) {
      const chartInstance = chartRef.current.getEchartsInstance();
      onChartReady(chartInstance);
      // Initially select the first legend (if any)
      if (Series.length > 0) {
        onLegendSelect(Series[Series.length - 1]);
      }
    }
  }, []);

  let decimalPoint = 2;
  if (
    label == 'Nitrogen Dioxide' ||
    label == 'Carbon Monoxide' ||
    label == 'Sulphur Dioxide' ||
    label == 'Ozone'
  ) {
    decimalPoint = 6;
  }

  return (
    <ReactEChartsCore
      ref={chartRef}
      echarts={echarts}
      option={getOptions(data, xAxis, Series, unit, decimalPoint)}
      onChartReady={onChartReady}
      style={{ height: '23.25rem' }}
    />
  );
};

LineChartBody.propTypes = {
  data: PropTypes.any.isRequired
};

export default LineChartBody;
